import { useEffect } from 'react';

import { getCurrentBreakpoint } from '@debijenkorf/app-shell';
import { i18n } from '@debijenkorf/dbk-common';
import { useProductCarouselTracking } from '@debijenkorf/product-carousel';
import { SectionHeader } from '@debijenkorf/text';

import {
  type CatalogProduct,
  ProductCarousel,
  mapCatalogProductToCarouselItem,
} from '@/components/Carousels/ProductCarousel';
import { useWishlist } from '@/hooks/wishlist/useWishlist';
import { DEFAULT_PLP_PATH } from '@/utilities/constants';
import { trackSearchResult } from '@/utilities/tracking';

export interface RelatedProductCarouselProps {
  relatedProducts: CatalogProduct[];
  correctionText: string;
  searchQuery: string;
  totalItemCount?: number;
}

const ITEM_PREFIX = 'related-product-carousel';
const HEADER_ID = `${ITEM_PREFIX}-header`;

export const RelatedProductCarousel = ({
  relatedProducts,
  correctionText,
  searchQuery,
  totalItemCount,
}: RelatedProductCarouselProps) => {
  const analyticsDataRelated = {
    products: relatedProducts,
    analyticsData: {
      location: 'no_search_results_lister',
      listName: 'carousel_cross_sell',
    },
  };

  const { ref, handleAnalyticsEvent } = useProductCarouselTracking(analyticsDataRelated);
  const { combinedProductCodes, onWishlistChange } = useWishlist(analyticsDataRelated);

  useEffect(() => {
    if (totalItemCount) {
      trackSearchResult({ searchTerm: correctionText, searchResult: totalItemCount });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SectionHeader
      aria-labelledby={HEADER_ID}
      title={`${totalItemCount || ''} ${i18n.t(
        'responsive-assets.plp.search.resultsFor',
      )} ”${correctionText}”`}
      cta={{
        title: `${i18n.t('responsive-assets.plp.search.viewAllResults')} "${correctionText}"`,
        href: `${DEFAULT_PLP_PATH}?${searchQuery}`,
      }}
    >
      <ProductCarousel
        ref={ref}
        itemLabelledbyPrefix={ITEM_PREFIX}
        aria-labelledby={HEADER_ID}
        items={relatedProducts.map(mapCatalogProductToCarouselItem)}
        currentBreakpoint={getCurrentBreakpoint()}
        withInlineSpace
        onWishlistChange={onWishlistChange}
        combinedProductCodes={combinedProductCodes}
        onAnalyticsEvent={handleAnalyticsEvent}
      />
    </SectionHeader>
  );
};
